import React, { useMemo, memo, useCallback, useRef } from 'react';
import style from "./searchBar.module.css";
import Select from 'react-select';

const SearchBar = memo(({ data, className: styleClass, optionHandle, menuPortalTarget = document.body, menuPosition, onFocus, onBlur, isMobileCollapsed }) => {
    const selectRef = useRef(null);

    // Ícone de lupa memoizado para evitar recriações em cada renderização
    const lupa = useCallback(() => (
        <svg className="search-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2c3e50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
    ), []);

    // Estilos para o react-select memoizados
    const customStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            border: '1px solid #ccc',
            borderRadius: '20px',
            paddingLeft: isMobileCollapsed ? '0px' : '10px',
            paddingRight: isMobileCollapsed ? '0px' : '10px',
            cursor: 'text',
            minHeight: '38px',
        }),
        valueContainer: (base) => ({
            ...base,
            width: isMobileCollapsed ? '0px' : 'auto',
            padding: isMobileCollapsed ? '0' : '2px 8px',
            overflow: 'hidden',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            width: isMobileCollapsed ? '100%' : 'auto',
            justifyContent: 'center'
        }),
        indicatorSeparator: (base) => ({
            ...base,
            display: isMobileCollapsed ? 'none' : 'flex',
            marginRight: '10px',
            marginLeft: '10px',
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0px 0px 10px 10px',
            marginTop: '2px',
            width: '95%', // Reduz a largura do menu
            marginLeft: '2.5%', // Centraliza o menu
            marginRight: '2.5%',
            zIndex: 9999,
        }),
        option: (base) => ({
            ...base,
            color: 'black',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 1002
        }),
    }), [isMobileCollapsed]);

    // Função de formatação de label memoizada
    const formatOptionLabel = useCallback(({ label, value }, { context }) => {
        const [title, variable] = label.split(': ');

        // Estilos diferentes para o título e para o valor
        return (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <span style={{ color: 'hsl(0, 0%, 60%)', textTransform: 'uppercase', fontSize: '75%', flex: 1, textAlign: 'left' }}>{title}</span>
                <span style={{ flex: 5, textAlign: 'center' }}>{variable}</span>
            </div>
        );
    }, []);

    // Handler para mudanças no select memoizado
    const handleChange = useCallback((selectedOption) => {
        optionHandle(selectedOption);
    }, [optionHandle]);

    // Componentes memoizados para o Select
    const components = useMemo(() => ({
        DropdownIndicator: lupa
    }), [lupa]);

    const handleContainerClick = useCallback(() => {
        if (onFocus) onFocus();
        if (selectRef.current) {
            selectRef.current.focus();
        }
    }, [onFocus]);

    return (
        <div className={`${style.searchBar_container} ${styleClass}`} onClick={handleContainerClick}>
            <Select
                ref={selectRef}
                isSearchable={true}
                styles={customStyles}
                formatOptionLabel={formatOptionLabel}
                components={components}
                placeholder="Pesquisar..."
                options={data}
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                // Otimizações adicionais para o react-select
                isOptionDisabled={(option) => option.isDisabled}
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                cacheOptions
                filterOption={(option, inputValue) => {
                    // Otimizar a filtragem para performance
                    if (!inputValue) return true;
                    const optValue = option.label.toLowerCase();
                    return optValue.includes(inputValue.toLowerCase());
                }}
                value={null}
            />
        </div>
    );
});

export default SearchBar;